<template>
  <v-container class="list-sessions">
    <v-card>
      <v-card-title class="mb-0">
        {{ title }}
      </v-card-title>
      <v-progress-linear v-if="loading" indeterminate />
      <v-card-text>
        <v-data-table
          class="my-data-table"
          :headers="computedHeaders.map((x) => { return { ...x, text: $t(x.text) } })"
          :items="sessionsMutated"
          item-key="id"
          :sort-by="['updated']"
          :sort-desc="[true]"
          :loading="loading"
        >
          <template v-slot:item.class="{ item }">
            <template v-if="item.classLearner">
              {{ classNames[item.classLearner].class.displayName }}
            </template>
          </template>

          <template v-slot:item.module="{ item }">
            {{ contentDisplayName(item.module) }}
          </template>

          <template v-slot:item.created="{ item }">
            <template v-if="item.created">
              {{ formatDateTime(item.created) }}
            </template>
          </template>

          <template v-slot:item.updated="{ item }">
            <template v-if="item.updated">
              {{ formatDateTime(item.updated) }}
            </template>
          </template>

          <template v-slot:item.completed="{ item }">
            <template v-if="item.completed">
              {{ formatDateTime(item.completed) }}
            </template>
          </template>

          <template v-slot:item.progress="{ item }">
            <v-chip class="status-chip" style="padding: 0;">
              <div style="height:100%; width: 220px;">
                <template v-if="loading">
                  <div style="display: flex; justify-content: center;">
                    <v-progress-circular 
                      indeterminate
                      color="green"
                      size="30"
                      width="5"
                    />
                  </div>
                </template>
                <template v-else>
                  <v-progress-linear 
                    :value="progressValues[item.id]" 
                    height="100%"
                    color="green"
                    class="status-progress-bar"
                    style="background-color: lightgreen; border-radius: 15px; "
                  />
                  <div class="status-text centered" style="color:white; font-size:small">
                    {{ progressValues[item.id] + '% COMPLETE' }}
                  </div>
                </template>
              </div>
            </v-chip>
          </template>

          <template v-slot:item.reports="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-if="item.module === 'IPE'" class="mr-2" v-on="on" @click="learnerReportClicked(item)">
                  mdi-file-document
                </v-icon>
              </template>
              <span>{{ $t('sessionList.view-report') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-if="hasCompletionCertificate(item)" v-on="on" @click="completionCertificateClicked(item)">
                  mdi-medal
                </v-icon>
              </template>
              <span>{{ $t('sessionList.certificate') }} </span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style scoped>
  .centered {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
</style>

<script>
  import api from '@/helpers/api.js'
  import sessionsAPI from '@/helpers/sessions.js'
  import moment from 'moment'
  import { ContentLibrary } from '@/helpers/contentLibrary.js'

  export default {
    name: 'ListSessions',
    props: {
      sessions: {
        type: Array,
        required: false,
        default: () => ([]),
      },
      showClass: {
        type: Boolean,
        required: false,
        default: false,
      },
      title: {
        type: String,
        required: false,
        default: () => 'Your session',
      },
      breadcrumbs: {
        type: Array,
        required: false,
        default: () => [],
      },
    },
    data () {
      return {
        moment: moment,
        loading: true,
        headers: [
          { text: 'sessionList.module', sortable: true, value: 'module' },
          { text: 'sessionList.progress', sortable: true, value: 'progress' },
          { text: 'sessionList.started', sortable: true, value: 'created' },
          { text: 'sessionList.updated', sortable: true, value: 'updated' },
          { text: 'sessionList.completed', sortable: true, value: 'completed' },
          { text: 'sessionList.actions', sortable: false, value: 'reports' },
        ],
        classNames: {},
        progressValues: {},
        contentLibrary: null,
      }
    },

    computed: {
      computedHeaders () {
        if (this.showClass) {
          return [
            { text: 'Class', value: 'class' },
            ...this.headers,
          ]
        } else {
          return this.headers
        }
      },
      sessionsMutated () {
        if (!this.contentLibrary || !this.sessions) return []
        const x = this.sessions.map(session => ({
          ...session,
          created: session.created ?? null,
          updated: session.updated ?? null,
          completed: session.completed ?? null,
          progress: this.progressValues[session.id],
        }))
        return x
      },
    },

    watch: {
      sessions: {
        handler (newVal) {
          this.calculateProgress()
        },
        deep: true,
      },
    },

    async mounted () {
      this.loading = true
      try {
        this.contentLibrary = await ContentLibrary.Get(this.$store, this.$i18n)
        this.calculateProgress()
        this.loading = false
      } catch (error) {
        console.error('Error in mounted hook:', error)
        this.loading = false
      }
    },

    methods: {
      learnerReportClicked (item) {
        this.redirectToLearnerReport(item.id, `${this.contentDisplayName(item.module)} (updated on ${this.formatDate(item.updated)})`)
      },
      completionCertificateClicked (item) {
        this.redirectToCompletionCertificate(item.id)
      },
      redirectToCompletionCertificate (sessionId) {
        window.location.href = '#/certificate/' + sessionId
      },
      calculateProgress () {
        if (!this.sessions) return
        for (const session of this.sessions) {
          if (session.classLearner && !this.classNames[session.classLearner]) {
            this.setClass(session.classLearner)
          }
          this.progressValues[session.id] = this.calculateSessionProgress(session)
        }
      },
      hasCompletionCertificate (item) {
        return item.completed
        // return (this.progressValues[item.id] >= 30)
      },
      calculateSessionProgress (session) {
        const moduleInfo = this.contentLibrary.find(x => x.id === session.module)
        const progress = sessionsAPI.calculateProgressFromSession(moduleInfo, session)
        this.$set(this.progressValues, session.id, progress)
        // console.log('session ' + session.id, ' progress: ' + progress, ' module: ' + session.module, ' completion: ' + session.completed)
        return progress
      },

      redirectToLearnerReport (sessionId, title) {
        this.$router.push({
          name: 'Learner Report',
          params: {
            sessionId: sessionId,
            breadcrumbs: this.breadcrumbs,
            title: title,
          },
        })
      },

      formatDateTime (timestamp) {
        return this.moment(timestamp).format('DD/MM/YYYY HH:mm')
      },

      formatDate (timestamp) {
        return this.moment(timestamp).format('DD/MM/YYYY')
      },

      contentDisplayName (contentID) {
        if (this.$store.state.contentLibrary === null) return ''
        if (contentID.length === 0) return this.$t('classes.no-module')
        if (Array.isArray(contentID) && contentID.length > 1) {
          return this.$t('classes.multiple-modules', { count: contentID.length })
        }
        if (Array.isArray(contentID)) contentID = contentID[0]
        return ContentLibrary.NameOf(this.$store, this.$i18n.locale, contentID.split('(')[0])
      },

      async setClass (classLearnerID) {
        this.$set(this.classNames, classLearnerID, await api.getClassLearner(classLearnerID))
      },
    },
  }
</script>

